import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { X, Hash, Save } from 'lucide-react';
import { LO_SHU_DATA, LO_SHU_GRID, MISSING_NUMBER_REMEDIES, NUMERO_REMEDY_LABELS } from './NumeroVastuDefaultData';
import { pickLang, LANGUAGE_OPTIONS } from './i18n/languageOptions';
import { loShuText, localDigits, nvLabel } from './NumeroVastuI18n';
import { calcKuaNumber, calcMoolank, calcBhagyank, calcNumerologyMatch, getLuckyColors, getUnluckyColors, KUA_DATA, kuaDir, kuaElement, kuaGroup, kuaPersonality, kuaLifeArea, kuaColors, kuaLabel as tKua } from './KuaNumberData';
import useIsMobile from '../../../hooks/useIsMobile';
import { useLeaveConfirm } from './Configure/components/ConfirmLeaveDialog';

// Not just the raw DOB digits — Moolank (birth number, from the day) and
// Bhagyank (destiny number, from the full DOB) are ALSO placed into the
// grid. A number derived only from Moolank/Bhagyank (never appearing bare
// in the DOB) still counts as present — e.g. DOB 16-10-2007 has no 8 among
// its own digits, but Bhagyank (1+6+1+0+2+0+0+7=17→8) puts an 8 in the
// grid, so 8 is NOT one of the missing numbers.
function calcLoShu(dob) {
  if (!dob) return { present: new Set(), missing: new Set() };
  const dobDigits = dob.replace(/\D/g, '').split('').map(Number).filter(d => d >= 1 && d <= 9);
  const moolank = calcMoolank(dob);
  const bhagyank = calcBhagyank(dob);
  const present = new Set(dobDigits);
  if (moolank) present.add(moolank);
  if (bhagyank) present.add(bhagyank);
  const missing = new Set();
  for (let i = 1; i <= 9; i++) { if (!present.has(i)) missing.add(i); }
  return { present, missing };
}

function buildDefaultHtml(num, remedies, lang = 'en') {
  const d = LO_SHU_DATA[num];
  const L = NUMERO_REMEDY_LABELS;
  let html = `<h2>${num} — ${pickLang(L.missingNumber, lang)}</h2>`;
  html += `<p><strong>${pickLang(L.direction, lang)}</strong> ${d.direction} &nbsp; <strong>${pickLang(L.planet, lang)}</strong> ${d.planet}</p>`;
  const sections = [
    { title: pickLang(L.mindRemedies, lang), items: remedies.mind },
    { title: pickLang(L.bodyRemedies, lang), items: remedies.body },
    { title: pickLang(L.soulRemedies, lang), items: remedies.soul },
  ];
  for (const s of sections) {
    if (s.items && s.items.length) {
      html += `<h3>${s.title}</h3><ul>`;
      for (const item of s.items) html += `<li>${pickLang(item, lang)}</li>`;
      html += '</ul>';
    }
  }
  return html;
}

const RichEditor = React.memo(function RichEditor({ value, onChange, editorKey }) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = value || '<p><br></p>';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorKey]);

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const formatBlock = useCallback((tag) => {
    editorRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const handleInput = useCallback(() => {
    onChange(editorRef.current?.innerHTML || '');
  }, [onChange]);

  const btn = (extra = {}) => ({
    border: '1px solid #e2e8f0', borderRadius: 4, background: '#fff', color: '#334155',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: 26, padding: '0 7px', fontSize: '0.74rem', fontFamily: 'inherit', ...extra,
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '5px 8px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <select onMouseDown={e => e.preventDefault()} onChange={e => { formatBlock(e.target.value); e.target.value = ''; }} defaultValue=""
          style={{ fontSize: '0.71rem', fontWeight: 600, border: '1px solid #e2e8f0', borderRadius: 4, padding: '2px 6px', color: '#334155', background: '#fff', cursor: 'pointer', outline: 'none', fontFamily: 'inherit', height: 26 }}>
          <option value="" disabled>Format</option>
          <option value="p">Paragraph</option>
          <option value="h2">H2 — Heading</option>
          <option value="h3">H3 — Sub-heading</option>
        </select>
        <span style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('bold'); }} style={btn({ fontWeight: 800, width: 26, padding: 0 })}>B</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('italic'); }} style={btn({ fontStyle: 'italic', width: 26, padding: 0 })}>I</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('underline'); }} style={btn({ textDecoration: 'underline', width: 26, padding: 0 })}>U</button>
        <span style={{ width: 1, height: 16, background: '#e2e8f0', margin: '0 2px' }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertOrderedList'); }} style={btn()}>1. List</button>
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('insertUnorderedList'); }} style={btn()}>• List</button>
        <span style={{ flex: 1 }} />
        <button type="button" onMouseDown={e => { e.preventDefault(); exec('removeFormat'); }} style={btn({ color: '#94a3b8', fontSize: '0.67rem' })}>Clear</button>
      </div>
      <div ref={editorRef} contentEditable suppressContentEditableWarning onInput={handleInput}
        data-placeholder="Type remedies here..." className="nvr-editor"
        style={{ minHeight: 160, padding: '12px 14px', outline: 'none', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", fontSize: '0.84rem', color: '#1e293b', lineHeight: 1.65 }} />
    </div>
  );
});

// Every cell always shows its full data (direction, planet, element, use) in
// its real color — a missing number gets a clean red X-mark drawn over it,
// not faded to gray with the "use" line dropped, which is what this used to
// do and didn't match the reference design.
function LoShuGridLarge({ present, isMobile, language = 'en' }) {
  // Mobile: 250px cells (3 across = ~760px) blew way past a phone viewport,
  // forcing horizontal scroll on the whole page. Shrunk cell + font sizes
  // only when isMobile; desktop keeps the original 250px cells untouched.
  const cellSize = isMobile ? 96 : 250;
  return (
    <div style={{ display: 'inline-grid', gridTemplateColumns: `repeat(3, ${cellSize}px)`, gap: isMobile ? 3 : 4 }}>
      {LO_SHU_GRID.flat().map(num => {
        const d = LO_SHU_DATA[num];
        const has = present.has(num);
        return (
          <div key={num} style={{
            width: cellSize, height: cellSize, background: d.color,
            borderRadius: isMobile ? 8 : 14, boxShadow: '0 2px 10px rgba(0,0,0,0.22)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            position: 'relative', overflow: 'hidden', padding: isMobile ? '4px' : '10px'
          }}>
            <div style={{ fontSize: isMobile ? 26 : 56, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{localDigits(num, language)}</div>
            <div style={{ fontSize: isMobile ? 9 : 15, color: 'rgba(255,255,255,0.95)', fontWeight: 700, marginTop: isMobile ? 3 : 8, textAlign: 'center' }}>{loShuText('direction', d.direction, language)}</div>
            <div style={{ fontSize: isMobile ? 8 : 12, color: 'rgba(255,255,255,0.82)', fontWeight: 500, marginTop: isMobile ? 1 : 2, textAlign: 'center' }}>{loShuText('planet', d.planet, language)}</div>
            <div style={{ fontSize: isMobile ? 7 : 11, color: 'rgba(255,255,255,0.7)', marginTop: 1, textAlign: 'center' }}>{loShuText('element', d.element, language)}</div>
            <div style={{ fontSize: isMobile ? 8 : 12, color: 'rgba(255,255,255,0.95)', fontWeight: 700, marginTop: isMobile ? 3 : 6, textAlign: 'center' }}>{loShuText('use', d.use, language)}</div>
            {!has && (
              <svg viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                <line x1="8" y1="8" x2="92" y2="92" stroke="#FF1111" strokeWidth="6" strokeLinecap="round" />
                <line x1="92" y1="8" x2="8" y2="92" stroke="#FF1111" strokeWidth="6" strokeLinecap="round" />
              </svg>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── KuaSummaryCard — Profile / Numerology / Favourable & Challenging
// Direction for one person, computed live from their DOB & gender. Read-only
// here (same data + calc as the editable Kua cards in the Persons/Numero
// gateway's "Kua Number" mode) — this screen shows it alongside the Lo Shu
// grid and zonal-strength table, which are display-only too.
const kuaRow = { display: 'grid', gridTemplateColumns: '220px 1fr', gap: 8, padding: '7px 16px', fontSize: 13, borderBottom: '1px solid #f1f5f9' };
const kuaLabel = { color: '#64748b', fontWeight: 600, fontSize: 12 };
const kuaVal = { color: '#0f172a' };
const kuaSectionHead = { padding: '10px 16px 6px', fontSize: 11, fontWeight: 800, color: '#00857E', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f0fdfa', borderTop: '1px solid #e2e8f0' };

function KuaSummaryCard({ person, isMobile, language = 'en' }) {
  const kua      = calcKuaNumber(person.dob, person.gender);
  const moolank  = calcMoolank(person.dob);
  const bhagyank = calcBhagyank(person.dob);
  const kd       = kua ? KUA_DATA[kua] : null;
  const match    = calcNumerologyMatch(moolank, bhagyank);
  const T        = (k) => tKua(k, language);

  // Mobile: the 220px label column left almost no room for the value column
  // on a ~360-400px phone. Stack label above value only when isMobile;
  // desktop keeps the original side-by-side 220px/1fr row untouched.
  const rowStyle     = isMobile ? { ...kuaRow, gridTemplateColumns: '1fr', gap: 2, padding: '7px 14px' } : kuaRow;
  const lastRowStyle = { ...rowStyle, borderBottom: 'none' };

  return (
    <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: '#0f172a' }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{person.name || 'Person'} — {T('analysisTitle')}</span>
      </div>

      <div style={kuaSectionHead}>{T('profile')}</div>
      <div style={rowStyle}><span style={kuaLabel}>{T('kuaNumber')}</span><span style={{ ...kuaVal, fontWeight: 700 }}>{kua ?? '—'}</span></div>
      {kd && <>
        <div style={rowStyle}><span style={kuaLabel}>{T('group')}</span><span style={kuaVal}>{kuaGroup(kd.group, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('element')}</span><span style={kuaVal}>{kuaElement(kd.element, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('personality')}</span><span style={kuaVal}>{kuaPersonality(kua, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('lifeArea')}</span><span style={kuaVal}>{kuaLifeArea(kua, language)}</span></div>
      </>}

      <div style={kuaSectionHead}>{T('numerology')}</div>
      <div style={rowStyle}><span style={kuaLabel}>{T('moolank')}</span><span style={{ ...kuaVal, fontWeight: 700 }}>{moolank ?? '—'}</span></div>
      <div style={rowStyle}><span style={kuaLabel}>{T('bhagyank')}</span><span style={{ ...kuaVal, fontWeight: 700 }}>{bhagyank ?? '—'}</span></div>
      {match && <>
        <div style={rowStyle}><span style={kuaLabel}>{T('luckyNumbers')}</span><span style={kuaVal}>{match.lucky.join(', ') || '—'}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('neutralNumbers')}</span><span style={kuaVal}>{match.neutral.join(', ') || '—'}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('unluckyNumbers')}</span><span style={kuaVal}>{match.unlucky.join(', ') || '—'}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('luckyColors')}</span><span style={kuaVal}>{kuaColors(getLuckyColors(moolank, bhagyank), language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('unluckyColors')}</span><span style={kuaVal}>{kuaColors(getUnluckyColors(moolank, bhagyank), language)}</span></div>
      </>}

      {kd && <>
        <div style={kuaSectionHead}>{T('favourableDirection')}</div>
        <div style={rowStyle}><span style={kuaLabel}>{T('shengChi')}</span><span style={kuaVal}>{kuaDir(kd.favorable.shengChi, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('tienYi')}</span><span style={kuaVal}>{kuaDir(kd.favorable.tianYi, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('nienYen')}</span><span style={kuaVal}>{kuaDir(kd.favorable.nienYen, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('fuWei')}</span><span style={kuaVal}>{kuaDir(kd.favorable.fuWei, language)}</span></div>

        <div style={kuaSectionHead}>{T('challengingDirection')}</div>
        <div style={rowStyle}><span style={kuaLabel}>{T('hoHai')}</span><span style={kuaVal}>{kuaDir(kd.challenging.hoHai, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('wuGui')}</span><span style={kuaVal}>{kuaDir(kd.challenging.wuGui, language)}</span></div>
        <div style={rowStyle}><span style={kuaLabel}>{T('liuSha')}</span><span style={kuaVal}>{kuaDir(kd.challenging.liuSha, language)}</span></div>
        <div style={lastRowStyle}><span style={kuaLabel}>{T('jueMing')}</span><span style={kuaVal}>{kuaDir(kd.challenging.jueMing, language)}</span></div>
      </>}
    </div>
  );
}

export default function NumeroVastuReport({ open, onClose, projectId, language = 'en', remedyData = {}, onRemedyChange, onSaved }) {
  // Mobile: this modal's header (title+subtitle+badge+3 text buttons), the
  // Lo Shu grid, the Kua summary rows, and the 6-column strength table were
  // all desktop-width assumptions that overflowed a phone screen. Fixed
  // below via isMobile-gated styles only; desktop keeps every original
  // style untouched.
  const isMobile = useIsMobile();
  const [persons, setPersons] = useState([]);
  const [savedMsg, setSavedMsg] = useState('');

  const personsKey = `vastu_persons_data_${projectId || 'new'}`;
  const fallbackKey = `numero_vastu_data_${projectId || 'new'}`;

  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem(personsKey) || localStorage.getItem(fallbackKey)
        || localStorage.getItem('vastu_persons_data') || localStorage.getItem('numero_vastu_data');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) { setPersons(parsed); }
      }
    } catch {}
  }, [open, personsKey, fallbackKey]);

  const loShuResults = useMemo(() =>
    persons.filter(p => p.dob).map(p => ({ ...p, ...calcLoShu(p.dob) })),
    [persons]
  );

  const totalPersons = loShuResults.length;

  const strengthData = useMemo(() => {
    if (totalPersons === 0) return [];
    const perPerson = 100 / totalPersons;
    return [1,2,3,4,5,6,7,8,9].map(num => {
      const count = loShuResults.filter(p => p.present.has(num)).length;
      const strength = parseFloat((count * perPerson).toFixed(1));
      return { num, ...LO_SHU_DATA[num], strength, count };
    });
  }, [loShuResults, totalPersons]);

  const weakNumbers = useMemo(() => strengthData.filter(s => s.strength < 50), [strengthData]);

  // True when `html` is exactly one of the 8 canonical default-language
  // renders for this number (i.e. nothing the user has typed over it yet).
  // Used below to tell "still default, safe to re-translate on a language
  // switch" apart from "user customised this, leave it alone".
  const isStillDefaultRemedy = useCallback((num, html) => {
    if (!html) return true;
    const remedies = MISSING_NUMBER_REMEDIES[num];
    if (!remedies) return false;
    return LANGUAGE_OPTIONS.some(({ code }) => buildDefaultHtml(num, remedies, code) === html);
  }, []);

  const getRemedyHtml = useCallback((num) => {
    if (remedyData[num]) return remedyData[num];
    const remedies = MISSING_NUMBER_REMEDIES[num];
    if (!remedies) return '';
    return buildDefaultHtml(num, remedies, language);
  }, [remedyData, language]);

  const handleRemedyChange = useCallback((num, html) => {
    if (onRemedyChange) onRemedyChange(prev => ({ ...prev, [num]: html }));
  }, [onRemedyChange]);

  // getRemedyHtml() falls back to auto-generated default text for display,
  // but that default was never actually written into remedyData — so unless
  // the user clicked into a box and typed something, PDF export (which only
  // reads remedyData) found nothing, for every weak number, saved or not.
  // Seed remedyData with the defaults so export always has real content —
  // and, whenever the report language changes, re-generate any entry that
  // is STILL a default (never hand-edited) into the newly selected
  // language, so switching the language dropdown actually switches this
  // predefined content instead of leaving it stuck in whatever language it
  // first got seeded in. Anything the user has typed their own words into
  // is left completely untouched.
  //
  // Deliberately NOT gated on `open` — this component stays mounted the
  // whole time (Configure/index.js renders it unconditionally, toggling
  // only its own `open` prop), so the language switch must keep working
  // while this modal is closed too: the user flips the toolbar's language
  // dropdown, then hits the main Export PDF button without reopening this
  // modal, and the export still needs the newly selected language's text.
  useEffect(() => {
    if (!onRemedyChange || weakNumbers.length === 0) return;
    const needsWork = weakNumbers.filter(w => isStillDefaultRemedy(w.num, remedyData[w.num]));
    if (needsWork.length === 0) return;
    onRemedyChange(prev => {
      const next = { ...prev };
      needsWork.forEach(w => {
        const remedies = MISSING_NUMBER_REMEDIES[w.num];
        if (!remedies) return;
        const translated = buildDefaultHtml(w.num, remedies, language);
        if (next[w.num] !== translated) next[w.num] = translated;
      });
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weakNumbers, language]);

  const leave = useLeaveConfirm({ onSave: () => { if (onSaved) onSaved(); onClose(); }, onLeave: onClose });

  if (!open) return null;

  return (
    /* z-index bumped above the mobile bottom toolbar (10100)/sheets — this
       used to render behind them on phone, looking like it never opened. */
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10500, display: 'flex', flexDirection: 'column',
      background: '#f8fafc', fontFamily: "'Inter','Segoe UI',system-ui,sans-serif", textAlign: 'left'
    }}>
      <style>{`
        .nvr-editor h2 { font-size:0.92rem; font-weight:800; color:#0f172a; margin:4px 0 4px; }
        .nvr-editor h3 { font-size:0.82rem; font-weight:700; color:#334155; margin:8px 0 2px; }
        .nvr-editor p { font-size:0.82rem; color:#475569; margin:1px 0 6px; line-height:1.65; }
        .nvr-editor ul, .nvr-editor ol { font-size:0.82rem; color:#475569; padding-left:1.8em; margin:2px 0 6px; line-height:1.65; }
        .nvr-editor li { margin-bottom:2px; }
        .nvr-editor strong { font-weight:700; color:#1e293b; }
        .nvr-editor:empty::before { content:attr(data-placeholder); color:#cbd5e1; pointer-events:none; }
        .nvr-editor:focus { outline:none; }
      `}</style>

      {/* Header */}
      <div style={{
        background: '#0f172a', color: '#fff', padding: isMobile ? '0 14px' : '0 24px', display: 'flex',
        alignItems: 'center', justifyContent: 'space-between', height: 56, flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14, minWidth: 0 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: '#00A19D', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Hash size={16} color="#fff" />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.94rem', fontWeight: 700, lineHeight: 1.2 }}>Numero Vastu Analysis</div>
            {!isMobile && <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Zonal Strength & Remedies</div>}
          </div>
          {!isMobile && (
            <>
              <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,.1)', margin: '0 4px' }} />
              <span style={{ fontSize: '0.72rem', background: 'rgba(0,161,157,.15)', color: '#00A19D', border: '1px solid rgba(0,161,157,.25)', borderRadius: 20, padding: '2px 10px', fontWeight: 600 }}>
                {totalPersons} person{totalPersons !== 1 ? 's' : ''}
              </span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 8, flexShrink: 0 }}>
          {savedMsg && !isMobile && <span style={{ fontSize: '0.76rem', color: '#4ade80', fontWeight: 500 }}>✓ {savedMsg}</span>}
          {isMobile ? (
            <button onClick={() => { setSavedMsg('Saved'); setTimeout(() => setSavedMsg(''), 3000); if (onSaved) onSaved(); }}
              title="Save"
              style={{ background: '#00A19D', border: 'none', color: '#fff', borderRadius: 7, width: 34, height: 34, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Save size={14} />
            </button>
          ) : (
            <>
              <button onClick={() => { setSavedMsg('Saved'); setTimeout(() => setSavedMsg(''), 3000); if (onSaved) onSaved(); }}
                style={{ background: '#00A19D', border: 'none', color: '#fff', borderRadius: 7, padding: '7px 16px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Save size={13} />Save
              </button>
              <button onClick={() => { if (onSaved) onSaved(); onClose(); }}
                style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.12)', color: '#fff', borderRadius: 7, padding: '7px 16px', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Save size={13} />Save &amp; Close
              </button>
            </>
          )}
          <button onClick={leave.requestClose} style={{
            background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.1)',
            color: '#94a3b8', borderRadius: 7, padding: isMobile ? '9px' : '7px 9px', cursor: 'pointer', display: 'flex', alignItems: 'center', flexShrink: 0
          }}>
            <X size={15} />
          </button>
          {leave.dialog}
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 0 60px' }}>
        {totalPersons === 0 ? (
          <div style={{ padding: '80px 20px', color: '#94a3b8', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>&#128290;</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>No birth date data found</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>Use "Add Birth Date" from the left sidebar to add persons.</div>
          </div>
        ) : (
          <div style={{ maxWidth: 820, margin: '0 auto', padding: isMobile ? '0 12px' : '0 20px' }}>
            {/* Lo Shu Grids — one per person, stacked vertically */}
            {loShuResults.map((p, idx) => (
              <div key={p.id || idx} style={{ marginBottom: 40 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 8, background: '#00A19D', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 15
                  }}>{idx + 1}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>{p.name || `Person ${idx + 1}`}</div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>{p.gender || 'Male'} &middot; {nvLabel('dob', language)}: {p.dob || 'N/A'}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <LoShuGridLarge present={p.present} isMobile={isMobile} language={language} />
                </div>
                <div style={{ marginTop: 20 }}>
                  <KuaSummaryCard person={p} isMobile={isMobile} language={language} />
                </div>
              </div>
            ))}

            {/* Divider */}
            <div style={{ height: 1, background: '#e2e8f0', margin: '32px 0' }} />

            {/* Strength Table */}
            <div style={{ marginBottom: 32 }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                {nvLabel('numberZonal', language)}
              </h3>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
                {nvLabel('tableIntro', language)}
              </p>
              <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflowX: isMobile ? 'auto' : 'hidden', overflowY: 'hidden' }}>
                <table style={{ width: '100%', minWidth: isMobile ? 600 : undefined, borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#0f172a' }}>
                      <th style={thStyle}>{nvLabel('number', language)}</th>
                      <th style={thStyle}>{nvLabel('direction', language)}</th>
                      <th style={thStyle}>{nvLabel('planet', language)}</th>
                      <th style={thStyle}>{nvLabel('element', language)}</th>
                      <th style={thStyle}>{nvLabel('lifeArea', language)}</th>
                      <th style={{ ...thStyle, minWidth: 150 }}>{nvLabel('strength', language)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {strengthData.map((s, i) => (
                      <tr key={s.num} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ ...tdStyle, fontWeight: 700 }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            width: 28, height: 28, borderRadius: 6, background: s.color, color: '#fff', fontWeight: 700, fontSize: 13
                          }}>{localDigits(s.num, language)}</span>
                        </td>
                        <td style={tdStyle}>{loShuText('direction', s.direction, language)}</td>
                        <td style={tdStyle}>{loShuText('planet', s.planet, language)}</td>
                        <td style={tdStyle}>{loShuText('element', s.element, language)}</td>
                        <td style={tdStyle}>{loShuText('use', s.use, language)}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ width: `${s.strength}%`, height: '100%', background: '#00A19D', borderRadius: 4 }} />
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 42, textAlign: 'right' }}>{s.strength}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Remedies with Editor */}
            {weakNumbers.length > 0 && (
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: '0 0 6px' }}>
                  {nvLabel('remediesFrom', language)}
                </h3>
                <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px' }}>
                  {nvLabel('weakZones', language)}
                </p>
                {weakNumbers.map(s => (
                  <div key={s.num} style={{
                    background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0',
                    marginBottom: 18, overflow: 'hidden'
                  }}>
                    <div style={{
                      background: '#0f172a', color: '#fff', padding: '12px 18px',
                      display: 'flex', alignItems: 'center', gap: 12
                    }}>
                      <span style={{
                        width: 32, height: 32, borderRadius: 7, background: s.color,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, fontWeight: 800, color: '#fff'
                      }}>{localDigits(s.num, language)}</span>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{localDigits(s.num, language)} — {String(pickLang(NUMERO_REMEDY_LABELS.missingNumber, language)).toUpperCase()}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                          {loShuText('direction', s.direction, language)} &middot; {loShuText('planet', s.planet, language)} &middot; {nvLabel('strength', language)}: {s.strength}%
                        </div>
                      </div>
                    </div>
                    <div style={{ padding: '14px 16px' }}>
                      <RichEditor
                        // Including `language` in the key forces the editor
                        // to re-sync its DOM from `value` on a language
                        // switch — otherwise it only re-reads `value` on
                        // mount, so a re-translated default would silently
                        // never appear in the box the user is looking at.
                        editorKey={`${s.num}-${language}`}
                        value={getRemedyHtml(s.num)}
                        onChange={html => handleRemedyChange(s.num, html)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {weakNumbers.length === 0 && (
              <div style={{ padding: '30px 20px', color: '#0f172a', textAlign: 'center', background: '#f0fdfc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{nvLabel('allStrong', language)}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>{nvLabel('noRemedies', language)}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const thStyle = { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' };
const tdStyle = { padding: '10px 12px', color: '#334155' };
